import { useOpenClose } from 'core/hooks/useOpenClose';
import { BorderedContainer } from '../../../common/BorderedContainer';
import {
  convertNumberToString,
  convertToPresenceAbsence,
  formatDateTime,
  formatTime,
} from '../../../common/converters';
import { SectionTitle } from '../../../common/SectionTitle';
import { InfoDisplay } from '../../common/InfoDisplay';
import { AccordionToggle } from '../common/AccordionToggle';
import { AccordionToggleContent } from '../common/AccordionToggleContent';
import { IncidentDetailPatient } from 'sections/niigata/features/IncidentDetail/useIncidentDetailForNiigata';

type Props = {
  /** バイタルリスト。 */
  vitals: IncidentDetailPatient['vitals'];
};

/** バイタルのコンポーネント。 */
export const Vitals = ({ vitals }: Props) => {
  return (
    <BorderedContainer>
      <div className="flex flex-col gap-[24px]">
        <SectionTitle text="バイタル" />
        <div className="flex flex-col gap-[8px]">
          {vitals.length > 0 &&
            vitals.map(vital => <Item key={vital.vitalId} vital={vital} />)}
        </div>
      </div>
    </BorderedContainer>
  );
};

type ItemProps = {
  /** バイタル。 */
  vital: IncidentDetailPatient['vitals'][number];
};

const Item = ({ vital }: ItemProps) => {
  const { isOpen, handleOpen, handleClose } = useOpenClose();

  return (
    <>
      <AccordionToggle
        isOpen={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
      >
        <p className="text-[16px] noto-sans-jp-medium text-textPrimary">
          {vital.data.recordedAt
            ? formatTime(vital.data.recordedAt)
            : '時間未指定'}
        </p>
      </AccordionToggle>
      <AccordionToggleContent isOpen={isOpen}>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="記録日時"
            content={formatDateTime(vital.data.recordedAt)}
          />
          <InfoDisplay
            title="測定タイミング"
            content={vital.data.measureTiming ?? ''}
          />
          <InfoDisplay title="JCS" content={vital.data.jcs ?? ''} />
          <InfoDisplay
            title="R"
            content={convertToPresenceAbsence(vital.data?.riaR)}
          />
          <InfoDisplay
            title="I"
            content={convertToPresenceAbsence(vital.data?.riaI)}
          />
          <InfoDisplay
            title="A"
            content={convertToPresenceAbsence(vital.data?.riaA)}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="E(GCS)" content={vital.data.gcsE ?? ''} />
          <InfoDisplay title="V(GCS)" content={vital.data.gcsV ?? ''} />
          <InfoDisplay title="M(GCS)" content={vital.data.gcsM ?? ''} />
          <InfoDisplay title="合計(GCS)" content={vital.data.gcsTotal ?? ''} />
          <InfoDisplay
            title="PR"
            content={convertNumberToString(vital.data?.pr)}
          />
          {/* アプリに存在しない */}
          <InfoDisplay
            title="不整"
            content={convertToPresenceAbsence(vital.data?.isArrhythmia)}
          />
          {/* アプリに存在しない */}
          <InfoDisplay
            title="触知不能"
            content={convertToPresenceAbsence(vital.data?.isUnpalpable)}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="BP(右)/収縮(mmHg)"
            content={convertNumberToString(vital.data?.sbpRight)}
          />
          <InfoDisplay
            title="BP(右)/拡張(mmHg)"
            content={convertNumberToString(vital.data?.dbpRight)}
          />
          <InfoDisplay
            title="BP(左)/収縮(mmHg)"
            content={convertNumberToString(vital.data?.sbpLeft)}
          />
          <InfoDisplay
            title="BP(左)/拡張(mmHg)"
            content={convertNumberToString(vital.data?.dbpLeft)}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="RR"
            content={
              vital.data.rr
                ? `${convertNumberToString(vital.data.rr)} 回/min`
                : ''
            }
          />
          <InfoDisplay
            title="Sp02(room air)"
            content={
              vital.data.spo2
                ? `${convertNumberToString(vital.data.spo2)} %`
                : ''
            }
          />
          <InfoDisplay
            title="Sp02(酸素投与)"
            content={
              vital.data.spo2After
                ? `${convertNumberToString(vital.data.spo2After)} %`
                : ''
            }
          />
          <InfoDisplay
            title="酸素投与"
            content={
              vital.data.o2Flow
                ? `${convertNumberToString(vital.data.o2Flow)} L`
                : ''
            }
          />
          <InfoDisplay title="器具" content={vital.data.o2Means ?? ''} />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="BT"
            content={
              vital.data.bt ? `${convertNumberToString(vital.data.bt)} °C` : ''
            }
          />
          <InfoDisplay
            title="体温測定場所"
            content={vital.data.btPart ?? ''}
          />
          <InfoDisplay
            title="心電図波形"
            content={getFormattedContent(vital.data.ecgWaveform)}
          />
          <InfoDisplay
            title="心電図補足"
            content={getFormattedContent(vital.data.ecgRemarks)}
          />
        </div>
        <InfoDisplay
          title="心電図補足コメント"
          content={vital.data.ecgRemarkComment ?? ''}
        />
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="瞳孔(左)"
            content={
              vital.data.pupilSizeLeft
                ? `${convertNumberToString(vital.data.pupilSizeLeft)} mm`
                : ''
            }
          />
          <InfoDisplay
            title="瞳孔(右)"
            content={
              vital.data.pupilSizeRight
                ? `${convertNumberToString(vital.data.pupilSizeRight)} mm`
                : ''
            }
          />
          <InfoDisplay
            title="対光反射(左)"
            content={vital.data.pupilReactionLeft ?? ''}
          />
          <InfoDisplay
            title="対光反射(右)"
            content={vital.data.pupilReactionRight ?? ''}
          />
        </div>
        <InfoDisplay
          title="処置・判断等"
          content={vital.data.vitalNote ?? ''}
        />
      </AccordionToggleContent>
    </>
  );
};

const getFormattedContent = (content: string | string[] | null) => {
  return Array.isArray(content) ? content.join(' ') : (content ?? '');
};
